import { createSlice } from '@reduxjs/toolkit'

export const TEXT_IF_MODES = {
    ANONYMOUS: 'anonymous',
    STORED: 'stored',
}

const initialState = {
    // Assignment info
    assignmentName: '',
    assignmentToken: '',
    instructions: '',
    questions: [], // { id, name, rubrics: [{ id, name }] }
    
    // Configuration
    showGrades: false, // Whether to display grade points in feedback
    pendingReview: false, // True when require_human_review is ON and TA hasn't approved yet
    requireHumanReview: false, // True when require_human_review setting is ON
    submitForGradeEnabled: false,
    maxIterations: null, // null means infinite (null for anonymous, null for require_human_review=ON)
    iterationsUsed: 0,
    
    // Submission state
    responses: {}, // { [questionId]: text }
    lastSubmittedResponses: {}, // Preserve submitted text for comparison
    
    // Feedback state
    submitted: false,
    feedbackResult: null, // TextInstantFeedbackOut from API
    cached: false, // Whether result was from cache
    isSubmittedForGrade: false, // True when already submitted for final grading
    
    // Loading states
    isLoading: false,
    isLoadingConfig: true,
    
    // Error state
    error: null,
    
    // UI state
    activeView: 'submit', // 'submit' | 'feedback'
    showInstructionsModal: false,
}

const textInstantFeedbackSlice = createSlice({
    name: 'textInstantFeedback',
    initialState,
    reducers: {
        // Assignment info
        setAssignmentInfo: (state, action) => {
            const { name, token, instructions, questions } = action.payload
            state.assignmentName = name || ''
            state.assignmentToken = token || ''
            state.instructions = instructions || ''
            state.questions = questions || []
        },
        
        // Configuration
        setConfig: (state, action) => {
            const { showGrades, submitForGradeEnabled, maxIterations, iterationsUsed, requireHumanReview } = action.payload
            state.showGrades = showGrades ?? false
            state.submitForGradeEnabled = submitForGradeEnabled ?? false
            state.requireHumanReview = requireHumanReview ?? false
            state.maxIterations = maxIterations ?? null
            state.iterationsUsed = iterationsUsed ?? 0
        },
        
        setPendingReview: (state, action) => {
            state.pendingReview = action.payload
        },
        
        // Response handling
        setResponse: (state, action) => {
            const { questionId, text } = action.payload
            state.responses[questionId] = text
        },
        
        setAllResponses: (state, action) => {
            state.responses = action.payload
        },
        
        // Submission state
        setSubmitted: (state, action) => {
            state.submitted = action.payload
        },
        
        setFeedbackResult: (state, action) => {
            state.feedbackResult = action.payload
            state.cached = action.payload?.cached ?? false
            state.showGrades = action.payload?.show_grades ?? state.showGrades
            state.pendingReview = action.payload?.pending_review ?? false
        },
        
        setIsSubmittedForGrade: (state, action) => {
            state.isSubmittedForGrade = action.payload
        },
        
        incrementIterations: (state) => {
            state.iterationsUsed += 1
        },
        
        // Loading states
        setIsLoading: (state, action) => {
            state.isLoading = action.payload
        },
        
        setIsLoadingConfig: (state, action) => {
            state.isLoadingConfig = action.payload
        },
        
        // Error handling
        setError: (state, action) => {
            state.error = action.payload
        },
        
        clearError: (state) => {
            state.error = null
        },
        
        // UI state
        setActiveView: (state, action) => {
            state.activeView = action.payload
        },
        
        setShowInstructionsModal: (state, action) => {
            state.showInstructionsModal = action.payload
        },
        
        // Preserve submitted responses for feedback view
        preserveSubmittedResponses: (state) => {
            state.lastSubmittedResponses = { ...state.responses }
        },
        
        // Reset for resubmission
        resetForEdit: (state) => {
            state.submitted = false
            state.feedbackResult = null
            state.activeView = 'submit'
            state.error = null
        },
        
        // Full reset
        reset: () => initialState,
    },
})

export const textInstantFeedbackActions = textInstantFeedbackSlice.actions

export const textInstantFeedbackSelectors = {
    selectAssignmentName: state => state.textInstantFeedback.assignmentName,
    selectAssignmentToken: state => state.textInstantFeedback.assignmentToken,
    selectInstructions: state => state.textInstantFeedback.instructions,
    selectQuestions: state => state.textInstantFeedback.questions,
    selectShowGrades: state => state.textInstantFeedback.showGrades,
    selectPendingReview: state => state.textInstantFeedback.pendingReview,
    selectRequireHumanReview: state => state.textInstantFeedback.requireHumanReview,
    selectSubmitForGradeEnabled: state => state.textInstantFeedback.submitForGradeEnabled,
    selectMaxIterations: state => state.textInstantFeedback.maxIterations,
    selectIterationsUsed: state => state.textInstantFeedback.iterationsUsed,
    selectIterationsRemaining: state => {
        const max = state.textInstantFeedback.maxIterations
        const used = state.textInstantFeedback.iterationsUsed
        return max === null ? null : Math.max(0, max - used)
    },
    selectCanSubmitFeedback: state => {
        const max = state.textInstantFeedback.maxIterations
        const used = state.textInstantFeedback.iterationsUsed
        return max === null || used < max
    },
    selectResponses: state => state.textInstantFeedback.responses,
    selectLastSubmittedResponses: state => state.textInstantFeedback.lastSubmittedResponses,
    selectSubmitted: state => state.textInstantFeedback.submitted,
    selectFeedbackResult: state => state.textInstantFeedback.feedbackResult,
    selectCached: state => state.textInstantFeedback.cached,
    selectIsSubmittedForGrade: state => state.textInstantFeedback.isSubmittedForGrade,
    selectIsLoading: state => state.textInstantFeedback.isLoading,
    selectIsLoadingConfig: state => state.textInstantFeedback.isLoadingConfig,
    selectError: state => state.textInstantFeedback.error,
    selectActiveView: state => state.textInstantFeedback.activeView,
    selectShowInstructionsModal: state => state.textInstantFeedback.showInstructionsModal,
}

export default textInstantFeedbackSlice.reducer


